import { AxiosResponse } from "axios";
import apiClient from "@/lib/api/client";
import { ApiResponse } from "@/types/auth";

export const isEmpty = (value: any): boolean =>
  value === undefined || value === null;

export const FetchApiGet = async <T = any>(
  url: string,
  params?: Record<string, any>,
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await apiClient.get(url, {
      params,
    });

    if (response.data.status !== "success") {
      throw new Error(
        response.data.message ||
          response.data.errorMessage ||
          "API call failed",
      );
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      const responseData = error.response.data;
      let errorMessage = "Server error";

      if (responseData) {
        if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.errorMessage) {
          errorMessage = responseData.errorMessage;
        } else if (responseData.error) {
          errorMessage = responseData.error;
        } else if (typeof responseData === "string") {
          errorMessage = responseData;
        } else {
          errorMessage = JSON.stringify(responseData);
        }
      }

      throw new Error(`API Error (${error.response.status}): ${errorMessage}`);
    } else if (error.request) {
      throw new Error("Network error: No response from server");
    } else {
      throw new Error(`Request error: ${error.message}`);
    }
  }
};

export const FetchApiPost = async <T = any>(
  url: string,
  params: Record<string, any>,
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await apiClient.post(
      url,
      params,
      {
        headers: { "Content-Type": "application/json" },
      },
    );

    if (response.data.status !== "success") {
      console.error("API returned non-success status:", response.data);
      throw new Error(
        response.data.errorMessage ||
          response.data.message ||
          "API call failed",
      );
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      const responseData = error.response.data;
      let errorMessage = "Server error";

      if (responseData) {
        if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.errorMessage) {
          errorMessage = responseData.errorMessage;
        } else if (responseData.error) {
          errorMessage = responseData.error;
        } else if (responseData.details) {
          errorMessage = JSON.stringify(responseData.details);
        } else if (typeof responseData === "string") {
          errorMessage = responseData;
        } else {
          errorMessage = JSON.stringify(responseData);
        }
      } else {
        errorMessage = error.response.statusText || "Unknown server error";
      }

      throw new Error(`API Error (${error.response.status}): ${errorMessage}`);
    } else if (error.request) {
      throw new Error("Network error: No response from server");
    } else {
      throw new Error(`Request error: ${error.message}`);
    }
  }
};

export const FetchApiPut = async <T = any>(
  url: string,
  params: Record<string, any>,
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await apiClient.put(
      url,
      params,
      {
        headers: { "Content-Type": "application/json" },
      },
    );

    if (response.data.status !== "success") {
      throw new Error(
        response.data.message ||
          response.data.errorMessage ||
          "API call failed",
      );
    }

    return response.data;
  } catch (error: any) {
    console.error("error", error);
    return {
      resultCode: error.status || 9999,
      errorMessage:
        error.response?.data?.message || error.message || "Unknown error",
    };
  }
};

export const FetchApiDelete = async <T = any>(
  url: string,
  params?: Record<string, any>,
  data?: any,
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await apiClient.delete(
      url,
      {
        params,
        data,
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.data.status !== "success") {
      throw new Error(
        response.data.message || response.data.errorMessage || "API 호출 실패",
      );
    }

    return response.data;
  } catch (error: any) {
    return {
      resultCode: 9999,
      errorMessage: error?.message || "Unknown error",
    };
  }
};

export const FetchApiUpload = async <T = any>(
  url: string,
  formData: FormData,
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await apiClient.post(
      url,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data.status !== "success") {
      throw new Error(
        response.data.message || response.data.errorMessage || "Upload failed",
      );
    }

    return response.data;
  } catch (error: any) {
    console.error("FetchApiUpload - Error details:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
    });

    throw error;
  }
};

export const getSearchParams = (searchRequest: Record<string, any>) => {
  return Object.entries(searchRequest || {})
    .filter(
      ([_, value]) => value !== undefined && value !== null && value !== "",
    )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");
};
